import {
  Icon,
  IconButton,
  Pagination,
  Panel,
  PanelContent,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@deque/cauldron-react';
import dayjs from 'dayjs';
import React, { type JSX } from 'react';
import { useTranslation } from 'react-i18next';
import {
  type EnterpriseFeatureFlagOverride,
  type SSOConfig
} from '~/common/api-client';
import type { OrNull } from '~/typings/utils';
import FeatureFlagToggle from '../containers/FeatureFlagToggle';
import UserIdLink from '../containers/UserIdLink';
import styles from './EnterpriseFeatureFlagsOverridesTable.css';

export type OnUpdateOverrideArgs = {
  state: boolean;
  overrideId: string;
};

export type EnterpriseFeatureFlagsOverridesTableProps = {
  overrides: EnterpriseFeatureFlagOverride[];
  total: number;
  currentPage: number;
  itemsPerPage: number;
  narrow?: boolean;
  sortBy: string;
  sortDirection: 'ASC' | 'DESC';
  isTogglingDisabled?: boolean;
  ssoConfig?: OrNull<SSOConfig>;
  onSort: (column: string) => void;
  onRemove: (overrideId: string) => void;
  onUpdate: (args: OnUpdateOverrideArgs) => Promise<void>;
  onNextPageClick: () => void;
  onPreviousPageClick: () => void;
  onFirstPageClick: () => void;
  onLastPageClick: () => void;
};

export type HeaderKey =
  | 'enterprise_id'
  | 'feature_id'
  | 'state'
  | 'created_by'
  | 'created_at'
  | 'updated_by'
  | 'updated_at';

export type TableHeaderItem = {
  key: HeaderKey;
  label: string;
};

export const tableHeaders: TableHeaderItem[] = [
  { key: 'enterprise_id', label: 'Enterprise ID' },
  { key: 'feature_id', label: 'Feature flag ID' },
  { key: 'state', label: 'Override value' },
  { key: 'created_by', label: 'Created by' },
  { key: 'created_at', label: 'Created at' },
  { key: 'updated_by', label: 'Updated by' },
  { key: 'updated_at', label: 'Updated at' }
];

const Timestamp: React.FC<{ date: Date }> = ({ date }) => {
  const dateTime = dayjs(date).toDate().toISOString();
  const formatted = React.useMemo(
    () => new Intl.DateTimeFormat('en').format(dayjs(date).toDate()),
    [date]
  );
  return <time dateTime={dateTime}>{formatted}</time>;
};

const EnterpriseFeatureFlagsOverridesTable = ({
  overrides,
  total,
  currentPage,
  itemsPerPage,
  narrow = false,
  sortBy,
  sortDirection,
  isTogglingDisabled = false,
  ssoConfig,
  onSort,
  onRemove,
  onNextPageClick,
  onPreviousPageClick,
  onFirstPageClick,
  onLastPageClick,
  onUpdate
}: EnterpriseFeatureFlagsOverridesTableProps): JSX.Element => {
  const { t } = useTranslation();

  const cauldronSortDirection = (column: string) => {
    if (sortBy !== column) {
      return 'none';
    }
    return sortDirection === 'ASC' ? 'ascending' : 'descending';
  };

  if (overrides.length === 0) {
    return <p>{t('No enterprise overrides found.')}</p>;
  }

  const handleTableHeaderSort = (key: HeaderKey) => {
    onSort(key);
  };

  const start = (currentPage - 1) * itemsPerPage + 1;
  const end = Math.min(start + itemsPerPage - 1, total);
  const paginationStatusLabel = (
    <span>
      {!narrow && t('Showing')} <strong>{start}</strong> –{' '}
      <strong>{end}</strong> {t('of')} <strong>{total}</strong>
    </span>
  );

  return (
    <>
      {!narrow ? (
        <Table className={styles.featureOverridesTable}>
          <TableHead>
            <TableRow>
              {tableHeaders.map(({ key, label }) => (
                <TableHeader
                  key={key}
                  scope="col"
                  onSort={() => handleTableHeaderSort(key)}
                  sortDirection={cauldronSortDirection(key)}
                >
                  {label}
                </TableHeader>
              ))}
              <TableHeader scope="col" className={styles.actionsColumnHeader}>
                {t('Actions')}
              </TableHeader>
            </TableRow>
          </TableHead>
          <TableBody>
            {overrides.map(override => (
              <TableRow key={override.id}>
                <TableHeader scope="row">{override.enterprise_id}</TableHeader>
                <TableCell className={styles.noWrapCell}>
                  {override.feature_id}
                </TableCell>
                <TableCell width="10%">
                  <FeatureFlagToggle
                    disabled={isTogglingDisabled}
                    onUpdate={({ id, state }) =>
                      onUpdate({ overrideId: id, state })
                    }
                    id={override.id}
                    isFeatureEnabled={override.state}
                  />
                </TableCell>
                <TableCell>
                  {override.created_by ? (
                    <UserIdLink
                      id={override.created_by}
                      ssoConfig={ssoConfig}
                    />
                  ) : (
                    <Icon type="no" label={t('no created_by value')} />
                  )}
                </TableCell>
                <TableCell>
                  <Timestamp date={override.created_at} />
                </TableCell>
                <TableCell>
                  {override.updated_by ? (
                    <UserIdLink
                      id={override.updated_by}
                      ssoConfig={ssoConfig}
                    />
                  ) : (
                    <Icon type="no" label={t('no updated_by value')} />
                  )}
                </TableCell>
                <TableCell>
                  {override.updated_at ? (
                    <Timestamp date={override.updated_at} />
                  ) : (
                    <Icon type="no" label={t('no updated_at value')} />
                  )}
                </TableCell>
                <TableCell>
                  <IconButton
                    icon="trash"
                    label={t(
                      'Remove the feature {{featureId}} override for enterprise {{enterpriseId}}',
                      {
                        featureId: override.feature_id,
                        enterpriseId: override.enterprise_id
                      }
                    )}
                    onClick={() => onRemove(override.id)}
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      ) : (
        <Panel padding={false}>
          <ul className={styles.mobileOverrideList}>
            {overrides.map(override => (
              <li key={override.id}>
                <PanelContent>
                  <h2>{t('Enterprise ID')}</h2>
                  <p>{override.enterprise_id}</p>

                  <h2>{t('Feature flag ID')}</h2>
                  <p>{override.feature_id}</p>

                  <h2>{t('Override value')}</h2>
                  <FeatureFlagToggle
                    disabled={isTogglingDisabled}
                    onUpdate={({ id, state }) =>
                      onUpdate({ overrideId: id, state })
                    }
                    id={override.id}
                    isFeatureEnabled={override.state}
                  />

                  <h2>{t('Created by')}</h2>
                  <p>
                    {override.created_by ? (
                      <UserIdLink
                        id={override.created_by}
                        ssoConfig={ssoConfig}
                      />
                    ) : (
                      <Icon type="no" label={t('no created_by value')} />
                    )}
                  </p>

                  <h2>{t('Created at')}</h2>
                  <p>
                    <Timestamp date={override.created_at} />
                  </p>

                  <h2>{t('Updated by')}</h2>
                  <p>
                    {override.updated_by ? (
                      <UserIdLink
                        id={override.updated_by}
                        ssoConfig={ssoConfig}
                      />
                    ) : (
                      <Icon type="no" label={t('no updated_by value')} />
                    )}
                  </p>

                  <h2>{t('Updated at')}</h2>
                  <p>
                    {override.updated_at ? (
                      <Timestamp date={override.updated_at} />
                    ) : (
                      <Icon type="no" label={t('no updated_at value')} />
                    )}
                  </p>

                  <h2>{t('Actions')}</h2>
                  <IconButton
                    icon="trash"
                    label={t(
                      'Remove the feature {{featureId}} override for enterprise {{enterpriseId}}',
                      {
                        featureId: override.feature_id,
                        enterpriseId: override.enterprise_id
                      }
                    )}
                    onClick={() => onRemove(override.id)}
                  />
                </PanelContent>
              </li>
            ))}
          </ul>
        </Panel>
      )}
      <Pagination
        totalItems={total}
        itemsPerPage={itemsPerPage}
        currentPage={currentPage}
        statusLabel={paginationStatusLabel}
        onNextPageClick={onNextPageClick}
        onPreviousPageClick={onPreviousPageClick}
        onFirstPageClick={onFirstPageClick}
        onLastPageClick={onLastPageClick}
      />
    </>
  );
};

export default EnterpriseFeatureFlagsOverridesTable;
